import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/global/SEO';
import { PageBuilder } from '../../components/global/PageBuilder';
import { getUrl, APIServiceConstants } from '../../redux/actions/BlueprintAPI';
import { IntegrationActionCreators } from '../../redux/actions/integrations';
import { MerchantActionCreators } from '../../redux/actions/merchant';

const TEN_SECONDS_IN_MILLISECONDS = 10000;

const { getMerchantData } = MerchantActionCreators;
const {
  getMerchantIntegrations,
  testBoldIntegration,
  resetBoldIntegrationState,
} = IntegrationActionCreators;

const IntegrationsHubBoldPage = () => {
  const dispatch = useDispatch();
  const { INTEGRATIONS } = APIServiceConstants;

  let installUrl;
  const [hasBoldInstallSubmitted, setHasBoldInstallSubmitted] = useState(false);
  const [isIntegrationActive, setIsIntegrationActive] = useState(false);

  const {
    integrationsLoading,
    integrationsLoaded,
    integrationsData,
    boldTestLoading,
    boldTestSuccess,
    boldTestError,
    merchantData,
    merchantLoaded,
  } = useSelector(
    (/** @type {import('../../redux/reducers').RootState} */state) => (
      { ...state.IntegrationsReducer, ...state.MerchantReducer }
    ),
  );

  const allDependenciesLoaded = merchantLoaded && integrationsLoaded;

  useEffect(() => {
    if (!integrationsLoaded && !integrationsLoading) {
      dispatch(getMerchantIntegrations());
    }
  }, [dispatch, integrationsLoaded, integrationsLoading]);

  useEffect(() => {
    if (!merchantLoaded) {
      dispatch(getMerchantData());
    }
  }, [merchantLoaded, dispatch]);

  useEffect(() => () => {
    dispatch(resetBoldIntegrationState());
  }, [dispatch]);

  useEffect(() => {
    let activationCheckInterval;

    if (integrationsLoaded) {
      if (hasBoldInstallSubmitted && !isIntegrationActive) {
        activationCheckInterval = setInterval(() => {
          if (!integrationsLoading) {
            dispatch(getMerchantIntegrations());
          }
        }, TEN_SECONDS_IN_MILLISECONDS);
      } if (hasBoldInstallSubmitted && isIntegrationActive && activationCheckInterval) {
        clearInterval(activationCheckInterval);
      }
    }

    return () => clearInterval(activationCheckInterval);
  }, [
    dispatch,
    integrationsLoaded,
    integrationsLoading,
    hasBoldInstallSubmitted,
    isIntegrationActive,
  ]);

  useEffect(() => {
    if (integrationsLoaded) {
      if (integrationsData.bold) {
        setIsIntegrationActive(integrationsData.bold.active);
      }
    }
  }, [integrationsData, integrationsLoaded]);

  const handleActivateClick = () => {
    setHasBoldInstallSubmitted(true);
    const cacheBusterSecondsSinceEpoch = (new Date()).getTime();
    installUrl = getUrl(INTEGRATIONS, `bold/install?merchantId=${merchantData.id}&cb=${cacheBusterSecondsSinceEpoch}`);

    return window.open(installUrl, '_blank');
  };

  const handleTestConnectionClick = () => dispatch(testBoldIntegration());

  return (
    <PageBuilder>
      <SEO title="Bold | Integrations | Blueprint" />
      <div className="a-view integrations-hub">
        <div className="container">
          <div className="row back-btn-container">
            <div className="col-md-12">
              <Link to="/integrations">Back</Link>
            </div>
          </div>

          <div className="top-bar">
            <h2 className="title">
              Bold Subscriptions
              {' '}
              {isIntegrationActive ? '✅' : ''}
            </h2>
          </div>

          {allDependenciesLoaded && isIntegrationActive && (
            <button className="btn btn-primary mr-2" onClick={handleTestConnectionClick} disabled={boldTestLoading} type="button">Test Connection</button>
          )}

          {allDependenciesLoaded && !isIntegrationActive && (
            <button className="btn btn-primary" onClick={handleActivateClick} type="button">Activate Connection</button>
          )}

          {boldTestSuccess && (
            <span>
              <span className="mr-1">✅</span>
              Connection OK!
            </span>
          )}

          {boldTestError && (
            <span>
              <span className="mr-1">🚨</span>
              {boldTestError}
            </span>
          )}

          {!allDependenciesLoaded && (
            <>Loading...</>
          )}

        </div>
      </div>
    </PageBuilder>
  );
};

export default memo(IntegrationsHubBoldPage);
